// downloadWeeklyExcel.js
import ExcelJS from "exceljs";
import { formatNumber } from "../numberingformat";
import BgnIcon from "../img/logobgn250.png";

async function fetchImageAsBase64(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return await new Promise((resolve) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result.split(",")[1]); // strip "data:..."
    fr.readAsDataURL(blob);
  });
}


// ===== helpers =====
const toInt = (v) => (v == null ? 0 : Number(v) || 0);
const fmtDayMonthID = (d) =>
  new Date(d).toLocaleDateString("id-ID", { day: "2-digit", month: "long" });

const ymdID = new Intl.DateTimeFormat("sv-SE", {
  timeZone: "Asia/Jakarta",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
const dateKeyLocal = (d) => (d ? ymdID.format(new Date(d)) : "0000-00-00");

// Colors (close to your PDF palette)
const HEX = {
  GOLD:   "D2A01E",
  RED:    "B43C3C",
  BROWN:  "966428",
  PURPLE: "011846",
  PLUM:   "8C506E",
  BANNER: "6E5028",
  WHITE:  "FFFFFF",
  BLACK:  "000000",
  DEEPBLUE:  "011846",
  MILDBLUE:  "B3E0E8",
};

const thinBorder = {
  top: { style: "thin", color: { argb: HEX.BLACK } },
  left: { style: "thin", color: { argb: HEX.BLACK } },
  bottom: { style: "thin", color: { argb: HEX.BLACK } },
  right: { style: "thin", color: { argb: HEX.BLACK } },
};

const center = { vertical: "middle", horizontal: "center" };
const right  = { vertical: "middle", horizontal: "right" };
const left   = { vertical: "middle", horizontal: "left" };

function setRowFill(row, fgColor) {
  row.eachCell((cell) => {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fgColor } };
    cell.font = { color: { argb: HEX.WHITE }, bold: true, size: 10, name: "Roboto" };
  });
}

function setCellsBorder(row) {
  row.eachCell((cell) => { cell.border = thinBorder; });
}

function setCellsFont(row, opts = {}) {
  row.eachCell((cell) => { cell.font = { name: "Roboto", size: 10, ...opts }; });
}

// Apply border to a rectangular cell range
function borderRange(ws, rowNum, fromCol, toCol) {
  for (let c = fromCol; c <= toCol; c++) {
    ws.getRow(rowNum).getCell(c).border = thinBorder;
  }
}


// ===== MAIN =====
// add `info` param with { namaSppg, kelurahan, kecamatan, kabkot, provinsi }
const downloadWeeklyExcel = async (data, info = {}) => {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Laporan Mingguan", {
    views: [{ showGridLines: false }],
    pageSetup: { orientation: "landscape", fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
    headerFooter: { oddFooter: "&C Halaman &P/&N" }, // center footer: Halaman P/N
  });

const namaSppg = "SPPG CISBINHAR"
const kelurahan = "CISARANTEN BINA HARAPAN"
const kecamatan = "ARCAMANIK"
const kabkot = "KOTA BANDUNG"
const provinsi = "JAWA BARAT"

// ===== NEW: Info table (2 cols) =====
function addInfoTableExcel(ws,) {
  // Ensure columns are wide enough for 2-col layout look
  if (!ws.columns || ws.columns.length < 9) {
    ws.columns = [
      { key: "A", width: 16  },
      { key: "B", width: 60 },
      { key: "C", width: 12 },
      { key: "D", width: 12 },
      { key: "E", width: 12 },
      { key: "F", width: 12 },
      { key: "G", width: 12 },
      { key: "H", width: 12 },
      { key: "I", width: 12 },
    ];
  } else {
    // Make sure value area is comfortably wide
    ws.getColumn(2).width = Math.max(ws.getColumn(2).width ?? 20, 40);
  }

  const rows = [
    ["NAMA SPPG",       `: ${data[0].kitchen_name}` || ""],
    ["ALAMAT",       `: ${data[0].kitchen_address}` || ""],
    // ["KELURAHAN/DESA",  `: ${kelurahan}`],
    // ["KECAMATAN",       `: ${kecamatan}`],
    // ["KABUPATEN/KOTA",  `: ${kabkot}`],
    // ["PROVINSI",        `: ${provinsi}`],
  ];
  console.log("Info table rows:", data[0]);

  rows.forEach(([label, val]) => {
    const r = ws.addRow([label, val]); // write to A & B
    // Merge B..I so the value spans across the sheet
    ws.mergeCells(r.number, 2, r.number, 9);

    // Styles: borders on A..I, bold label, small font, left alignment
    r.getCell(1).font = { name: "Roboto", size: 10, bold: true, color: { argb: HEX.BLACK } };
    r.getCell(2).font = { name: "Roboto", size: 10, color: { argb: HEX.BLACK } };

    r.getCell(1).alignment = left;
    r.getCell(2).alignment = left;

    // Borders
    r.getCell(1).border = thinBorder;
    borderRange(ws, r.number, 2, 9);

    // Compact height like PDF small text
    r.height = 18;
  });

  // spacer row after the table
  ws.addRow([]);
}
  // Column layout (A..I)
  ws.columns = [
    { key: "no", width: 24 },
    { key: "nama", width: 30 },
    { key: "kes13", width: 14 },
    { key: "kes46", width: 14 },
    { key: "kesJml", width: 14 },
    { key: "real13", width: 14 },
    { key: "real46", width: 14 },
    { key: "realJml", width: 14 },
    { key: "ket", width: 28 },
  ];

  // ===== group by week_no =====
  const weeks = Object.values(
    data.reduce((acc, row) => {
      const w = row.week_no ?? "UNKNOWN";
      (acc[w] ||= { weekNo: w, rows: [] }).rows.push(row);
      return acc;
    }, {})
  ).sort((a, b) => Number(a.weekNo) - Number(b.weekNo));

  let isFirstWeek = true;
  let fileFirstStart = null;
  let fileLastEnd = null;

  for (const week of weeks) {
    // Period text for this week
    const dates = week.rows
      .map((r) => new Date(r.date))
      .filter((d) => !isNaN(d))
      .sort((a, b) => a - b);

    const periodeStartText = dates.length ? fmtDayMonthID(dates[0]) : "-";
    const periodeEndText   = dates.length ? fmtDayMonthID(dates[dates.length - 1]) : "-";

    if (!fileFirstStart && dates.length) fileFirstStart = dates[0];
    if (dates.length) fileLastEnd = dates[dates.length - 1];

    // Page break between weeks (except first)
    if (!isFirstWeek) {
      ws.addRow([]);
      ws.addRow([]);
      ws.addPageBreak(ws.lastRow);
    }
    isFirstWeek = false;

    // ==== HEADER (3 centered lines)
const startRow = ws.lastRow ? ws.lastRow.number + 1 : 1;

// Insert logo on the left, aligned with header rows (columns B..C, rows startRow..startRow+2)
const logoB64 = await fetchImageAsBase64(BgnIcon);
const imageId = wb.addImage({ base64: logoB64, extension: "png" });
ws.addImage(imageId, {
  tl: { col: 1.2, row: startRow - 0.8 }, // top-left position
  ext: { width: 80, height: 80 },        // size in pixels
});


    ws.mergeCells(startRow, 1, startRow, 9);
    ws.mergeCells(startRow + 1, 1, startRow + 1, 9);
    ws.mergeCells(startRow + 2, 1, startRow + 2, 9);

    const r1 = ws.getRow(startRow);
    r1.getCell(1).value = "LAPORAN REALISASI PAKET MBG";
    r1.height = 18;
    r1.alignment = center;
    setCellsFont(r1, { bold: true, size: 12 });

    const r2 = ws.getRow(startRow + 1);
    r2.getCell(1).value = `PERIODE ${periodeStartText} s.d. ${periodeEndText}`;
    r2.alignment = center;
    setCellsFont(r2, { bold: true, size: 10 });

    const r3 = ws.getRow(startRow + 2);
    r3.getCell(1).value = `MINGGU KE-${week.weekNo}`;
    r3.alignment = center;
    setCellsFont(r3, { bold: true, size: 10 });

    ws.addRow([]);
    ws.addRow([]);

    // >>> NEW: Info table directly under the header <<<
    addInfoTableExcel(ws, info);

    // ==== group by DATE within this week
    const groupedByDate = week.rows.reduce((acc, row) => {
      const key = dateKeyLocal(row.date);
      (acc[key] ||= []).push(row);
      return acc;
    }, {});

    const dateKeys = Object.keys(groupedByDate).sort((a, b) => a.localeCompare(b));

    // Render helpers
    const addSectionBanner = (title) => {
      const row = ws.addRow([title, "", "", "", "", "", "", "", ""]);
      ws.mergeCells(row.number, 1, row.number, 9);
      row.height = 20;
      row.alignment = center;
      setRowFill(row, HEX.DEEPBLUE);
      setCellsBorder(row);
    };

    const addTwoRowHeader = (prettyDate, nameColTitle) => {
      // Row 1
      const rTop = ws.addRow([
        "NO", nameColTitle, "KESELURUHAN", "", "", `REALISASI ${prettyDate}`, "", "", "KETERANGAN",
      ]);
      ws.mergeCells(rTop.number, 1, rTop.number + 1, 1); // NO
      ws.mergeCells(rTop.number, 2, rTop.number + 1, 2); // NAMA
      ws.mergeCells(rTop.number, 3, rTop.number + 1, 5);     // KESELURUHAN
      ws.mergeCells(rTop.number, 6, rTop.number + 1, 8);     // REALISASI
      ws.mergeCells(rTop.number, 9, rTop.number + 1, 9); // KETERANGAN
      setCellsBorder(rTop);
      setCellsFont(rTop, { bold: true, color: { argb: HEX.BLACK }, size: 10 });
      // Colors
      rTop.getCell(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEX.MILDBLUE } };
      rTop.getCell(2).fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEX.MILDBLUE } };
      rTop.getCell(3).fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEX.MILDBLUE } };
      rTop.getCell(4).fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEX.MILDBLUE } };
      rTop.getCell(5).fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEX.MILDBLUE } };
      rTop.getCell(6).fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEX.MILDBLUE } };
      rTop.getCell(7).fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEX.MILDBLUE } };
      rTop.getCell(8).fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEX.MILDBLUE } };
      rTop.getCell(9).fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEX.MILDBLUE } };
      rTop.alignment = center;

      // Row 2
      const rBot = ws.addRow([
        "", "",
        "Siswa Kelas 1-3", "Siswa Kelas 4-6", "Jumlah",
        "Siswa Kelas 1-3", "Siswa Kelas 4-6", "Jumlah",
        ""
      ]);
      setCellsBorder(rBot);
      setCellsFont(rBot, { bold: true, color: { argb: HEX.BLACK }, size: 10 });
      rBot.getCell(3).fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEX.MILDBLUE } };
      rBot.getCell(4).fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEX.MILDBLUE } };
      rBot.getCell(5).fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEX.MILDBLUE } };
      rBot.getCell(6).fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEX.MILDBLUE } };
      rBot.getCell(7).fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEX.MILDBLUE } };
      rBot.getCell(8).fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEX.MILDBLUE } };
      rBot.alignment = center;
    };

    const addDataRows = (rows) => {
      rows.forEach((r) => {
        const rr = ws.addRow(r);
        setCellsBorder(rr);
        rr.getCell(1).alignment = center;
        rr.getCell(2).alignment = left;
        for (let c = 3; c <= 8; c++) rr.getCell(c).alignment = right;
        rr.getCell(9).alignment = left;
        setCellsFont(rr, { size: 10 });
      });
    };

    const addTotalsRow = (totals) => {
      const row = ws.addRow([
        "TOTAL", "",
        formatNumber(totals.kes13),
        formatNumber(totals.kes46),
        formatNumber(totals.kesJml),
        formatNumber(totals.real13),
        formatNumber(totals.real46),
        formatNumber(totals.realJml),
        "",
      ]);
      setCellsBorder(row);
      row.eachCell((cell, col) => {
        cell.font = { name: "Roboto", bold: true, size: 10, color: { argb: HEX.BLACK } };
        if (col >= 3 && col <= 5) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEX.MILDBLUE } };
        if (col >= 6 && col <= 8) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEX.MILDBLUE } };
        if (col <= 2 || col === 9) cell.font = { name: "Roboto", bold: true, size: 10, color: { argb: HEX.BLACK } };
      });
      row.getCell(1).alignment = center;
      row.getCell(2).alignment = center;
      for (let c = 3; c <= 8; c++) row.getCell(c).alignment = right;
    };

    const renderBlock = (title, nameColTitle, items, prettyDate) => {
      if (!items.length) return;

      // Banner
      addSectionBanner(title);
      // Header 2 rows
      addTwoRowHeader(prettyDate, nameColTitle);

      // body rows (formatted numbers)
      const rows = items.map((it, i) => {
        const kes13 = toInt(it.kelas_13_sch);
        const kes46 = toInt(it.kelas_46_sch);
        const kesJml = toInt(it.jumlah_sch ?? kes13 + kes46);
        const real13 = toInt(it.kelas_13_real);
        const real46 = toInt(it.kelas_46_real);
        const realJml = toInt(it.jumlah_real ?? real13 + real46);

        return [
          formatNumber(i + 1),
          it.customer_name || "-",
          formatNumber(kes13),
          formatNumber(kes46),
          formatNumber(kesJml),
          formatNumber(real13),
          formatNumber(real46),
          formatNumber(realJml),
          "",
        ];
      });

      addDataRows(rows);

      // totals (numeric from rows)
      const totals = {
        kes13: rows.reduce((s, r) => s + toInt(r[2]), 0),
        kes46: rows.reduce((s, r) => s + toInt(r[3]), 0),
        kesJml: rows.reduce((s, r) => s + toInt(r[4]), 0),
        real13: rows.reduce((s, r) => s + toInt(r[5]), 0),
        real46: rows.reduce((s, r) => s + toInt(r[6]), 0),
        realJml: rows.reduce((s, r) => s + toInt(r[7]), 0),
      };
      addTotalsRow(totals);


    };

    // === iterate dates ===
    for (const key of dateKeys) {
      const items = groupedByDate[key];
      const prettyDate = new Date(key).toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "long",
      }).toUpperCase();

      const sekolahItems = items.filter((x) => x.customer_type === "SEKOLAH");
      const posyanduItems = items.filter((x) => x.customer_type === "POSYANDU");

      renderBlock("JUMLAH SISWA SEKOLAH", "NAMA SEKOLAH", sekolahItems, prettyDate);
      renderBlock("JUMLAH POSYANDU", "NAMA POSYANDU", posyanduItems, prettyDate);

      if (sekolahItems.length || posyanduItems.length) {
        // grand total for that date
        const agg = (arr, pick) => arr.reduce((s, it) => s + toInt(it[pick]), 0);
        const s13 = agg(sekolahItems, "kelas_13_sch") + agg(posyanduItems, "kelas_13_sch");
        const s46 = agg(sekolahItems, "kelas_46_sch") + agg(posyanduItems, "kelas_46_sch");
        const sJm = agg(sekolahItems, "jumlah_sch")   + agg(posyanduItems, "jumlah_sch");
        const r13 = agg(sekolahItems, "kelas_13_real") + agg(posyanduItems, "kelas_13_real");
        const r46 = agg(sekolahItems, "kelas_46_real") + agg(posyanduItems, "kelas_46_real");
        const rJm = agg(sekolahItems, "jumlah_real")   + agg(posyanduItems, "jumlah_real");

        const row = ws.addRow([
          "JUMLAH KESELURUHAN", "",
          formatNumber(s13),
          formatNumber(s46),
          formatNumber(sJm),
          formatNumber(r13),
          formatNumber(r46),
          formatNumber(rJm),
          "",
        ]);
        setCellsBorder(row);
        row.eachCell((cell, col) => {
          cell.font = { name: "Roboto", bold: true, size: 10, color: { argb: HEX.BLACK } };
          if (col >= 3 && col <= 5) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEX.MILDBLUE } };
          if (col >= 6 && col <= 8) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEX.MILDBLUE } };
          if (col <= 2 || col === 9) cell.font = { name: "Roboto", bold: true, size: 10, color: { argb: HEX.BLACK } };
        });
        row.getCell(1).alignment = center;
        row.getCell(2).alignment = center;
        for (let c = 3; c <= 8; c++) row.getCell(c).alignment = right;

        ws.addRow([]);
      }
    }
  }

  // filename
  const rangeStart = fileFirstStart ? ymdID.format(fileFirstStart) : "start";
  const rangeEnd   = fileLastEnd ? ymdID.format(fileLastEnd) : "end";
  const filename = `Laporan_Mingguan_${rangeStart}_s.d._${rangeEnd}.xlsx`;

  // write & download
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export default downloadWeeklyExcel;
